import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import {
  mobileTimeWheelFormat,
  mobileTimeWheelMinuteValues,
  mobileTimeWheelPad2,
  mobileTimeWheelParse,
  mobileTimeWheelSnapMinute,
} from "./mobile-time-wheel.util";

const ITEM_PX = 40;

@Component({
  selector: "mobile-time-wheel",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./mobile-time-wheel.component.html",
  styleUrl: "./mobile-time-wheel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MobileTimeWheelComponent),
      multi: true,
    },
  ],
})
export class MobileTimeWheelComponent implements ControlValueAccessor, OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly mobileTimeWheelPad2 = mobileTimeWheelPad2;

  @Input({ required: true }) inputId!: string;
  @Input() ariaLabel = "";
  @Input() placeholder = "--:--";
  /** Traduction ngx-translate pour le bouton (défaut : confirmer). */
  @Input() confirmKey = "common.confirm";
  /** Intervalle entre minutes affichées (1 = toutes les minutes). */
  @Input() minuteStep = 1;
  /** Clé i18n pour l’aria du bouton horloge ; si vide, on retombe sur `ariaLabel`. */
  @Input() clockHintKey = "";

  readonly hours = Array.from({ length: 24 }, (_, i) => i);

  sheetOpen = signal(false);
  draftHour = signal(0);
  draftMinute = signal(0);

  @ViewChild("hourScroll") hourScroll?: ElementRef<HTMLElement>;
  @ViewChild("minuteScroll") minuteScroll?: ElementRef<HTMLElement>;

  private internalValue = "";
  disabled = false;

  minuteOpts: number[] = mobileTimeWheelMinuteValues(1);

  private hourScrollTimer?: ReturnType<typeof setTimeout>;
  private minuteScrollTimer?: ReturnType<typeof setTimeout>;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  displayValue(): string {
    return this.internalValue.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["minuteStep"]) {
      this.minuteOpts = mobileTimeWheelMinuteValues(this.minuteStep);
    }
  }

  writeValue(value: string | null): void {
    this.internalValue = typeof value === "string" ? value : "";
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  openSheet(): void {
    if (this.disabled) {
      return;
    }
    this.minuteOpts = mobileTimeWheelMinuteValues(this.minuteStep);
    const parsed = mobileTimeWheelParse(this.internalValue);
    const now = new Date();
    let hour = parsed?.hour ?? now.getHours();
    let baseM = parsed?.minute ?? now.getMinutes();
    let minute = mobileTimeWheelSnapMinute(baseM, this.minuteStep);
    minute = this.nearestMinuteOption(minute);
    hour = Math.max(0, Math.min(23, hour));
    this.draftHour.set(hour);
    this.draftMinute.set(minute);
    this.sheetOpen.set(true);
    this.cdr.markForCheck();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.alignScrollBarsFromDraft();
        this.cdr.markForCheck();
      });
    });
  }

  private alignScrollBarsFromDraft(): void {
    const hourScrollElement = this.hourScroll?.nativeElement;
    const minuteScrollElement = this.minuteScroll?.nativeElement;
    if (!hourScrollElement || !minuteScrollElement) {
      return;
    }
    const hour = Math.max(0, Math.min(23, this.draftHour()));
    const rawIdx = this.minuteOpts.findIndex((minuteOption) => minuteOption === this.draftMinute());
    const mIdx = rawIdx >= 0 ? rawIdx : 0;
    hourScrollElement.scrollTop = hour * ITEM_PX;
    minuteScrollElement.scrollTop = mIdx * ITEM_PX;
  }

  private nearestMinuteOption(minute: number): number {
    let best = this.minuteOpts[0] ?? 0;
    let bestDist = Infinity;
    for (const minuteOption of this.minuteOpts) {
      const distance = Math.abs(minuteOption - minute);
      if (distance < bestDist) {
        bestDist = distance;
        best = minuteOption;
      }
    }
    return best;
  }

  cancelSheet(): void {
    this.sheetOpen.set(false);
    this.cdr.markForCheck();
  }

  confirmSheet(): void {
    if (!this.sheetOpen()) {
      return;
    }
    const formattedValue = mobileTimeWheelFormat(this.draftHour(), this.draftMinute());
    this.internalValue = formattedValue;
    this.onChange(formattedValue);
    this.onTouched();
    this.sheetOpen.set(false);
    this.cdr.markForCheck();
  }

  onBackdrop(): void {
    this.cancelSheet();
  }

  @HostListener("document:keydown", ["$event"])
  onDocKey(ev: KeyboardEvent): void {
    if (ev.key !== "Escape" || !this.sheetOpen()) {
      return;
    }
    this.cancelSheet();
    ev.preventDefault();
  }

  onHourScroll(): void {
    if (this.hourScrollTimer !== undefined) {
      clearTimeout(this.hourScrollTimer);
    }
    this.hourScrollTimer = setTimeout(() => this.snapHourColumn(), 64);
  }

  onHourScrollEnd(): void {
    if (this.hourScrollTimer !== undefined) {
      clearTimeout(this.hourScrollTimer);
      this.hourScrollTimer = undefined;
    }
    this.snapHourColumn();
  }

  onMinuteScroll(): void {
    if (this.minuteScrollTimer !== undefined) {
      clearTimeout(this.minuteScrollTimer);
    }
    this.minuteScrollTimer = setTimeout(() => this.snapMinuteColumn(), 64);
  }

  onMinuteScrollEnd(): void {
    if (this.minuteScrollTimer !== undefined) {
      clearTimeout(this.minuteScrollTimer);
      this.minuteScrollTimer = undefined;
    }
    this.snapMinuteColumn();
  }

  private snapHourColumn(): void {
    const hourScrollElement = this.hourScroll?.nativeElement;
    if (!hourScrollElement) {
      return;
    }
    let idx = Math.round(hourScrollElement.scrollTop / ITEM_PX);
    idx = Math.max(0, Math.min(23, idx));
    hourScrollElement.scrollTop = idx * ITEM_PX;
    this.draftHour.set(idx);
    this.cdr.markForCheck();
  }

  private snapMinuteColumn(): void {
    const minuteScrollElement = this.minuteScroll?.nativeElement;
    if (!minuteScrollElement) {
      return;
    }
    const maxIdx = Math.max(0, this.minuteOpts.length - 1);
    let idx = Math.round(minuteScrollElement.scrollTop / ITEM_PX);
    idx = Math.max(0, Math.min(maxIdx, idx));
    minuteScrollElement.scrollTop = idx * ITEM_PX;
    const minute = this.minuteOpts[idx] ?? 0;
    this.draftMinute.set(minute);
    this.cdr.markForCheck();
  }
}
